import type { TPaginationMeta } from '@doutorsim/shared/pagination'
import type { TPublicProblemDetails } from '@doutorsim/shared/problem-details'
import { env } from '@/env'

import { useCompanyStore } from '../../_stores/company.store'

const API_URL = env.VITE_API_URL

export interface ApiRequestOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>
  organizationId?: string
}

type ApiSuccess<T> = { data: T; meta?: TPaginationMeta; error?: never }
type ApiFailure = { error: TPublicProblemDetails; data?: never; meta?: never }
export type ApiResult<T> = ApiSuccess<T> | ApiFailure

export class ApiClient {
  private baseURL: string

  constructor(baseURL?: string) {
    this.baseURL = baseURL ?? API_URL

    if (!this.baseURL) {
      throw new Error('ApiClient: baseURL is required')
    }
  }

  private getHeaders(options?: ApiRequestOptions): HeadersInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options?.headers,
    }

    if (options?.organizationId) {
      headers['X-Organization-Id'] = options.organizationId
    } else {
      const companyId = useCompanyStore.getState().company?.id
      if (companyId) {
        headers['X-Organization-Id'] = companyId
      }
    }

    return headers
  }

  private async request<T>(endpoint: string, options?: ApiRequestOptions): Promise<ApiResult<T>> {
    try {
      const headers = this.getHeaders(options)
      const url = `${this.baseURL}${endpoint}`

      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      })

      if (!response.ok) {
        const errorBody = await response.json().catch((e) => {
          console.error('Failed to parse error response:', e)
          return null
        })

        const { type, title, status, detail, instance, ...extensions } = errorBody ?? {}

        return {
          error: {
            type: type ?? 'about:blank',
            title: title ?? 'Unknown Error',
            status: status ?? response.status,
            detail,
            instance,
            ...(Object.keys(extensions).length > 0 && { extensions }),
          },
        }
      }

      const json = await response.json()

      const { data, meta } = json

      return {
        data: data ?? json,
        ...(meta !== undefined && { meta }),
      }
    } catch (error) {
      return {
        error: {
          type: 'about:blank',
          title: 'Network Error',
          status: 0,
          detail: error instanceof Error ? error.message : 'Failed to connect to server',
        },
      }
    }
  }

  async get<T>(endpoint: string, options?: ApiRequestOptions): Promise<ApiResult<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: ApiRequestOptions,
  ): Promise<ApiResult<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    options?: ApiRequestOptions,
  ): Promise<ApiResult<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    options?: ApiRequestOptions,
  ): Promise<ApiResult<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async delete<T>(endpoint: string, options?: ApiRequestOptions): Promise<ApiResult<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }
}
